import { HttpClient, ListParams } from './http-client';
import { Observable } from 'rxjs';
import Paginate from '../interfaces/paginate';
import Field from '../interfaces/field';
import FieldHistory, {
  FieldHistoryDataAnalysesTable,
  FieldHistoryDataMergeTable,
  FieldHistoryDataOwnershipTable,
  FieldHistoryFertilizationTable,
  FieldHistoryFieldProtectionTable,
  FieldHistoryOverviewTable,
  FieldHistorySeedTable,
  FieldHistoryTable,
  FieldHistoryWorkTable,
  FieldHistoryYieldTable,
} from '../interfaces/field-history';
import { FieldStatus } from '../enums/field-status';
import { FieldHistoryTabs } from '../enums/field-history-tabs';
import { Option } from '../components/form/elements/select';
import { FieldForSync } from '../pages/crop/fields-and-works/fields/basic-info/components/synchronize-context';

export class FieldService extends HttpClient {
  index(
    farmId: string,
    season: string,
    params: ListParams,
    getAll = false
  ): Observable<Paginate<Field>> {
    return this.get<Paginate<Field>>(
      `/api/farms/${farmId}/season/${season}/fields`,
      { ...params, getAll }
    );
  }

  totalArea(farmId: string, season: string): Observable<number> {
    return this.get<number>(
      `/api/farms/${farmId}/season/${season}/fields-total-area`
    );
  }

  show(farmId: string, id: string, params?: { [key: string]: any }) {
    return this.get<Field>(`/api/farms/${farmId}/fields/${id}`, params);
  }

  store(farmId: string, field: Field) {
    return this.post<Field>(`/api/farms/${farmId}/fields`, field);
  }

  update(farmId: string, id: string, field: Field) {
    return this.put<Field>(`/api/farms/${farmId}/fields/${id}`, field);
  }

  fieldHistory(farmId: string, id: string): Observable<Paginate<FieldHistory>> {
    return this.get<Paginate<FieldHistory>>(
      `/api/farms/${farmId}/fields/${id}/history`
    );
  }

  getCropGroupFilterValues(farmId: string, season: string) {
    return this.get<any[]>(
      `/api/farms/${farmId}/season/${season}/crop-filter-values`
    );
  }

  getLadNumbersFilterValues(farmId: string, season: string) {
    return this.get<Option[]>(
      `/api/farms/${farmId}/season/${season}/lad-number-filter-values`
    );
  }

  getBlockNumbersFilterValues(farmId: string, season: string) {
    return this.get<Option[]>(
      `/api/farms/${farmId}/season/${season}/block-number-filter-values`
    );
  }

  getNameFilterValues(farmId: string, season: string) {
    return this.get<Option[]>(
      `/api/farms/${farmId}/season/${season}/name-filter-values`
    );
  }

  exportXls(farmId: string, season: string, params: ListParams) {
    return this.export(`/api/farms/${farmId}/season/${season}/fields`, params);
  }

  merge(farmId: string, season: string, field: Field) {
    return this.post<Field>(
      `/api/farms/${farmId}/season/${season}/fields/merge`,
      field
    );
  }

  split(farmId: string, season: string, fieldId: string, fields: Field[]) {
    return this.post<Field>(
      `/api/farms/${farmId}/season/${season}/fields/${fieldId}/split`,
      fields
    );
  }

  ladApiDataStatus(farmId: string, season: string) {
    return this.get(`/api/farms/${farmId}/season/${season}/fields/lad/status`);
  }

  getFieldDataFromLad(
    farmId: string,
    season: string,
    params: ListParams
  ): Observable<Paginate<Field>> {
    return this.get<Paginate<Field>>(
      `/api/farms/${farmId}/season/${season}/fields/lad/importable`,
      params
    );
  }

  importFieldsFromLad(farmId: string, season: string): Observable<boolean> {
    return this.post(`/api/farms/${farmId}/season/${season}/fields/lad/import`);
  }

  fieldEditIsAllowed(field: Field): boolean {
    // only fields with following statuses are allowed to be edited
    return [
      FieldStatus.CREATED_MANUALLY.toString(),
      FieldStatus.CREATED_BY_MERGE.toString(),
      FieldStatus.CREATED_BY_SPLIT.toString(),
      FieldStatus.IMPORTED_FROM_LAD.toString(),
      FieldStatus.DISABLED_AFTER_EXPIRED_MERGE.toString(),
      FieldStatus.DISABLED_AFTER_EXPIRED_SPLIT.toString(),
      FieldStatus.RESTORED_AFTER_EXPIRED_MERGE.toString(),
      FieldStatus.RESTORED_AFTER_EXPIRED_SPLIT.toString(),
    ].includes(field.field_status);
  }

  historyTable(
    farmId: string,
    season: string,
    params: ListParams
  ): Observable<Paginate<FieldHistoryTable>> {
    return this.get<Paginate<FieldHistoryTable>>(
      `/api/farms/${farmId}/season/${season}/field-history`,
      params
    );
  }

  historyTableUsedProductCategories(
    farmId: string,
    season: string
  ): Observable<Option[]> {
    return this.get<Option[]>(
      `/api/farms/${farmId}/season/${season}/field-history-used-product-categories`
    );
  }

  historyOverview(
    farmId: string,
    season: string,
    dataset: string
  ): Observable<Paginate<FieldHistoryOverviewTable>> {
    return this.get<Paginate<FieldHistoryOverviewTable>>(
      `/api/farms/${farmId}/season/${season}/field-history-overview`,
      { dataset }
    );
  }

  fieldHistorySection(
    section: FieldHistoryTabs,
    farmId: string,
    season: string,
    fieldId: string,
    params: ListParams
  ): Observable<Paginate<FieldHistoryWorkTable>> {
    return this.get<Paginate<FieldHistoryWorkTable>>(
      `/api/farms/${farmId}/season/${season}/field-history/${fieldId}/${section}`,
      params
    );
  }

  fieldHistorySectionCropProtection(
    farmId: string,
    season: string,
    fieldId: string,
    params: ListParams
  ): Observable<Paginate<FieldHistoryFieldProtectionTable>> {
    return this.get<Paginate<FieldHistoryFieldProtectionTable>>(
      `/api/farms/${farmId}/season/${season}/field-history/${fieldId}/${FieldHistoryTabs.CROP_PROTECTION}`,
      params
    );
  }

  fieldHistorySectionFertilization(
    farmId: string,
    season: string,
    fieldId: string,
    params: ListParams
  ): Observable<Paginate<FieldHistoryFertilizationTable>> {
    return this.get<Paginate<FieldHistoryFertilizationTable>>(
      `/api/farms/${farmId}/season/${season}/field-history/${fieldId}/${FieldHistoryTabs.FERTILIZATION}`,
      params
    );
  }

  fieldHistorySectionSeed(
    farmId: string,
    season: string,
    fieldId: string,
    params: ListParams
  ): Observable<Paginate<FieldHistorySeedTable>> {
    return this.get<Paginate<FieldHistorySeedTable>>(
      `/api/farms/${farmId}/season/${season}/field-history/${fieldId}/${FieldHistoryTabs.SEED}`,
      params
    );
  }

  fieldHistorySectionYield(
    farmId: string,
    season: string,
    fieldId: string,
    params: ListParams
  ): Observable<Paginate<FieldHistoryYieldTable>> {
    return this.get<Paginate<FieldHistoryYieldTable>>(
      `/api/farms/${farmId}/season/${season}/field-history/${fieldId}/${FieldHistoryTabs.YIELD}`,
      params
    );
  }

  fieldHistorySectionDataAnalysis(
    farmId: string,
    season: string,
    fieldId: string
  ): Observable<Paginate<FieldHistoryDataAnalysesTable>> {
    return this.get<Paginate<FieldHistoryDataAnalysesTable>>(
      `/api/farms/${farmId}/season/${season}/field-history/${fieldId}/${FieldHistoryTabs.DATA}-analysis`
    );
  }

  fieldHistorySectionDataOwnership(
    farmId: string,
    season: string,
    fieldId: string
  ): Observable<Paginate<FieldHistoryDataOwnershipTable>> {
    return this.get<Paginate<FieldHistoryDataOwnershipTable>>(
      `/api/farms/${farmId}/season/${season}/field-history/${fieldId}/${FieldHistoryTabs.DATA}-ownership`
    );
  }

  fieldHistorySectionMergeHistory(
    farmId: string,
    season: string,
    fieldId: string
  ): Observable<Paginate<FieldHistoryDataMergeTable>> {
    return this.get<Paginate<FieldHistoryDataMergeTable>>(
      `/api/farms/${farmId}/season/${season}/field-history/${fieldId}/${FieldHistoryTabs.DATA}-merges`
    );
  }

  exportFieldHistoryXls(farmId: string, season: string, params: ListParams) {
    return this.export(
      `/api/farms/${farmId}/season/${season}/field-history`,
      params
    );
  }

  ladFieldsThatNotMatch(
    farmId: string,
    season: string
  ): Observable<Paginate<FieldForSync>> {
    return this.get<Paginate<FieldForSync>>(
      `/api/farms/${farmId}/season/${season}/fields/lad/not-match`
    );
  }

  notRegisteredInLad(
    farmId: string,
    season: string
  ): Observable<Paginate<FieldForSync>> {
    return this.get<Paginate<FieldForSync>>(
      `/api/farms/${farmId}/season/${season}/fields/lad/not-registered`
    );
  }

  currentStatusWithLad<T>(
    farmId: string,
    season: string
  ): Observable<Paginate<T>> {
    return this.get<Paginate<T>>(
      `/api/farms/${farmId}/season/${season}/fields/lad/current-status`
    );
  }

  storedWithGeo(
    farmId: string,
    season: string
  ): Observable<Paginate<FieldForSync>> {
    return this.get<Paginate<FieldForSync>>(
      `/api/farms/${farmId}/season/${season}/fields/lad/with-geo`
    );
  }

  synchronizeWithLad<T>(
    data: T,
    farmId: string,
    season: string
  ): Observable<boolean> {
    return this.post(
      `/api/farms/${farmId}/season/${season}/fields/lad/synchronize`,
      data
    );
  }
}
